import { matchesNestedKeyword } from '../../shared/utils/keyword-filter';
import { JstDate } from '../../core/time/business-time';
import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DatePipe, DecimalPipe } from '@angular/common';
import { PageTitleService } from '../../core/services/page-title.service';
import { StudioService } from '../../core/services/studio.service';
import { MemberService } from '../../core/services/member.service';
import { TransactionService } from '../../core/services/transaction.service';
import { InvoiceService } from '../../core/services/invoice.service';
import { FlashService } from '../../core/services/flash.service';
import { FlashMessageComponent } from '../../shared/components/flash-message.component';
import { StudioSelectionService } from '../../core/services/studio-selection.service';
import { environment } from '../../../environments/environment';
import { PagerComponent } from '../../shared/components/pager.component';
import { TransactionEditComponent } from './transaction-edit.component';

const SESSION_KEY = 'transaction_select_studio_id';

function buildYearOptions(): { id: number; name: string }[] {
  const now = new JstDate();
  const options = [];
  for (let y = 2015; y <= now.getFullYear() + 1; y++) {
    options.push({ id: y, name: `${y}年` });
  }
  return options;
}

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  id: i + 1,
  name: `${i + 1}月`,
}));

const CHARGE_TYPE_OPTIONS = [
  { id: 1, name: '月会費' },
  { id: 2, name: '入会金・事務手数料' },
  { id: 3, name: 'ロッカー費' },
  { id: 4, name: 'PT費' },
  { id: 5, name: '物販' },
  { id: 0, name: 'その他' },
];

const PAYMENT_METHOD_OPTIONS = [
  { id: '0', name: '口座振替' },
  { id: '1', name: 'クレジットカード' },
];

const STATUS_OPTIONS = [
  { id: 0, name: '請求予定' },
  { id: 1, name: '削除済' },
  { id: 2, name: '請求確定' },
  { id: 5, name: '入金済' },
  { id: 6, name: '入金エラー' },
  { id: 7, name: '払い戻し済' },
  { id: 8, name: '請求停止' },
  { id: 9, name: '受け取り' },
];

function chargeTypeName(id: number): string {
  return CHARGE_TYPE_OPTIONS.find(o => o.id === id)?.name ?? '';
}

@Component({
    selector: 'app-transaction-list',
    imports: [RouterLink, FormsModule, DatePipe, DecimalPipe, PagerComponent, TransactionEditComponent, FlashMessageComponent],
    template: `
<div class="animated fadeIn container-fluid">
  <div class="card">
    <div class="card-header">
      <span><i class="fa s-icon-list"></i> 一覧 <span>(全 {{ filteredItems.length }} 件)</span></span>
    </div>
    <div class="card-body">
      <!-- 旧版 transaction.html:12-14 の card-body 先頭 <flash-message> 相当 -->
      <div class="row"><app-flash-message class="col-md-12"></app-flash-message></div>
      <form novalidate>
        <div class="row">
          <div class="col-md-4">
            <div class="form-group">
              <label class="col-form-label" for="studio_name">店舗</label>
              <select class="form-control" id="studio_name" [(ngModel)]="selectedStudio"
                name="studio_name" (ngModelChange)="onStudioChange()">
                <option [ngValue]="null">-- 選択してください --</option>
                @for (studio of studios; track studio.id) {
                  <option [ngValue]="studio">{{ studio.name }}</option>
                }
              </select>
            </div>
          </div>
          @if (showFilterOption) {
            <div class="col-md-4">
              <div class="form-group">
                <label class="col-form-label" for="charge_type">売上タイプ</label>
                <select class="form-control" id="charge_type" [(ngModel)]="chargeTypeFilter" name="charge_type" (ngModelChange)="pageIndex=0">
                  <option [ngValue]="null">-- すべて --</option>
                  @for (opt of chargeTypeOptions; track opt.id) {
                    <option [ngValue]="opt">{{ opt.name }}</option>
                  }
                </select>
              </div>
            </div>
          }
          <div class="col-md-4">
            <div class="form-group">
              <label class="col-form-label" for="year_pay">売上年月</label>
              <div class="bisected-input-wrap">
                <div class="bisected-input">
                  <select class="form-control" id="year_pay" [(ngModel)]="selectedYear"
                    name="year_pay" (ngModelChange)="loadTransactions()">
                    @for (opt of yearOptions; track opt.id) {
                      <option [ngValue]="opt.id">{{ opt.name }}</option>
                    }
                  </select>
                </div>
                <div><span>&nbsp;</span></div>
                <div class="bisected-input">
                  <select class="form-control" id="month_pay" [(ngModel)]="selectedMonth"
                    name="month_pay" (ngModelChange)="loadTransactions()">
                    @for (opt of monthOptions; track opt.id) {
                      <option [ngValue]="opt.id">{{ opt.name }}</option>
                    }
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
        @if (showFilterOption) {
          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label class="col-form-label" for="pay_method">支払方法</label>
                <select class="form-control" id="pay_method" [(ngModel)]="paymentMethodFilter" name="pay_method" (ngModelChange)="pageIndex=0">
                  <option [ngValue]="null">-- すべて --</option>
                  @for (opt of paymentMethodOptions; track opt.id) {
                    <option [ngValue]="opt">{{ opt.name }}</option>
                  }
                </select>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label class="col-form-label" for="status">状態</label>
                <select class="form-control" id="status" [(ngModel)]="statusFilter" name="status" (ngModelChange)="pageIndex=0">
                  <option [ngValue]="null">-- すべて --</option>
                  @for (opt of statusOptions; track opt.id) {
                    <option [ngValue]="opt">{{ opt.name }}</option>
                  }
                </select>
              </div>
            </div>
          </div>
          <div class="row" style="align-items: flex-end;">
            <div class="col-md-8">
              <div class="form-group">
                <label class="col-form-label" for="keyword">キーワード検索</label>
                <input id="keyword" name="keyword" type="text" [(ngModel)]="keyword" (ngModelChange)="pageIndex=0"
                  class="form-control" placeholder="会員ID、会員氏名など">
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <button class="btn btn-warning text-white" type="button" (click)="clearFilter()">
                  <i class="fa s-icon-backspace fa-fw"></i>&nbsp;条件クリア
                </button>
              </div>
            </div>
          </div>
        }
        <hr>
      </form>
    </div>
    <div class="card-body">
      @if (showFilterOption) {
        <div class="form-group row">
          <div class="col-md-12">
            <button class="btn btn-success" type="button" (click)="downloadPaymentErrorList()" [disabled]="downloading">
              <i class="fa fa-cloud-download"></i>未納リストダウンロード
            </button>
          </div>
        </div>
      }
      @if (searching) {
        <div class="text-center py-3"><span class="fa fa-spinner fa-spin fa-2x"></span></div>
      }
      @if (selectedStudio && !searching && filteredItems.length === 0) {
        <span>該当するデータはありません</span>
      }
      @if (filteredItems.length > 0) {
        <app-pager [pageIndex]="pageIndex" [totalPages]="totalPages" (pageChange)="goToPage($event)"></app-pager>
        <table class="table table-bordered table-sm table-hover table-ptb-0">
          <thead>
            <tr>
              <th style="width:3%;"></th>
              <th>状態</th>
              <th>会員ID</th>
              <th>会員氏名</th>
              <th>売上年月</th>
              <th>売上タイプ</th>
              <th>明細</th>
              <th>金額</th>
              <th>支払方法</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            @for (item of pagedItems; track item.id) {
              <tr>
                <td class="text-center text-white"
                  [class.bg-warning]="item.status === 6 && item.invoice?.retryCount === 0"
                  [class.bg-danger]="item.status === 6 && item.invoice?.retryCount > 0 && item.invoice?.retryCount !== 9 && item.invoice?.retryCount !== 99">
                  @if (item.status === 6 && item.invoice?.retryCount !== 9 && item.invoice?.retryCount !== 99) {
                    <span>{{ (item.invoice?.retryCount ?? 0) + 1 }}</span>
                  }
                </td>
                <td class="text-center">
                  <div class="d-flex flex-column bd-highlight text-center">
                    @if (item.status === 0) { <span class="text-warning" title="請求予定"><i class="fa s-icon-schedule fa-2x fa-fw"></i></span> }
                    @if (item.status === 1) { <span class="text-secondary" title="削除済"><i class="fa s-icon-close fa-2x fa-fw"></i></span> }
                    @if (item.status === 2) { <span class="text-warning" title="請求確定"><i class="fa s-icon-waiting fa-2x fa-fw"></i></span> }
                    @if (item.status === 3) { <span class="text-primary" title="承認済"><i class="fa fa-gavel fa-2x fa-fw"></i></span> }
                    @if (item.status === 4) { <span class="text-secondary" title="キャンセル済"><i class="fa fa-times-circle fa-2x fa-fw"></i></span> }
                    @if (item.status === 5) {
                      <span [class.text-success]="!item.isOvercharged" [class.text-danger]="item.isOvercharged" title="入金済">
                        <i class="fa s-icon-finished fa-2x fa-fw"></i>
                      </span>
                    }
                    @if (item.status === 6) { <span class="text-danger" title="入金エラー"><i class="fa s-icon-attention fa-2x fa-fw"></i></span> }
                    @if (item.status === 7) { <span class="text-success" title="払い戻し済"><i class="fa s-icon-back fa-2x fa-fw"></i></span> }
                    @if (item.status === 8) { <span class="text-secondary" title="請求停止"><span class="material-icons-outlined fa-2x fa-fw">do_disturb_on</span></span> }
                    @if (item.status === 9) { <span class="text-success" title="受け取り"><i class="fa s-icon-cashPay fa-2x fa-fw"></i></span> }
                    <div>
                      @if (item.status === 0 || item.status === 6) {
                        <button type="button" class="btn btn-primary text-white btn-sm ml-1 mr-1 mb-1"
                          style="padding-top:0.1rem !important;padding-bottom:0.1rem !important;"
                          (click)="openPayInCashModal(item)" title="受け取り処理">
                          <i class="fa s-icon-settlement fa-fw"></i>
                        </button>
                      }
                      @if (item.status === 0 || item.status === 3 || item.status === 6) {
                        <button type="button" class="btn btn-danger text-white btn-sm ml-1 mr-1 mb-1"
                          style="padding-top:0.1rem !important;padding-bottom:0.1rem !important;"
                          (click)="openDeleteModal(item)">
                          <i class="fa fa-trash fa-fw"></i>
                        </button>
                      }
                    </div>
                  </div>
                </td>
                <td>{{ item.member?.memberCode }}</td>
                <td>
                  <a href="" (click)="$event.preventDefault(); openMemberDetail(item.member?.id)">
                    {{ item.member?.lastName }}&nbsp;{{ item.member?.firstName }}
                  </a>
                </td>
                <td>{{ item.yearPay }}年{{ item.monthPay }}月</td>
                <td>{{ chargeTypeName(item.chargeType) }}</td>
                <td>{{ item.title }}</td>
                <td class="text-right">¥{{ item.amount | number:'1.0-0' }}</td>
                <td>
                  @if (item.paymentMethod === 0 || item.member?.payment?.paymentMethod === 0) { 口座振替 }
                  @if (item.paymentMethod === 1 || item.member?.payment?.paymentMethod === 1) { クレジット }
                </td>
                <td class="text-center">
                  @if (item.status === 0 || item.status === 2 || item.status === 5 || item.status === 6 || item.status === 7) {
                    <button type="button" class="mt-1 mb-1 btn btn-primary btn-sm text-white"
                      (click)="openEditTransactionModal(item)" title="編集">
                      <i class="fa s-icon-edit fa-fw"></i>
                    </button>
                  }
                  <a class="mt-1 mb-1 btn btn-warning btn-sm text-white"
                    [routerLink]="['/transaction', item.member?.id, 'all']" title="売上一覧">
                    <i class="fa s-icon-salesList fa-fw"></i>
                  </a>
                </td>
              </tr>
            }
          </tbody>
        </table>
        <app-pager [pageIndex]="pageIndex" [totalPages]="totalPages" (pageChange)="goToPage($event)"></app-pager>
      }
    </div>
  </div>
</div>
<!-- 受け取り処理モーダル -->
@if (showPayInCashModal) {
  <div class="modal-backdrop fade show"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content border-danger">
        <div class="modal-header bg-danger">
          <h5 class="modal-title"><i class="fa s-icon-attention"></i> 受け取り処理</h5>
          <button type="button" class="close" (click)="showPayInCashModal = false">
            <span class="text-white" aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body" style="background-color: #FFF6F8 !important">
          <div class="form-group row">
            <label class="col-md-12 col-form-label">変更ボタンを押すと状態にかかわらず、<br>関係するすべての請求が「受け取り」となります。</label>
          </div>
          <div class="form-group">
            <table class="table table-sm table-borderless">
              <tr><th class="text-right" style="width:20%;">会員ID：</th><td>{{ payInCashItem?.member?.memberCode }}</td></tr>
              <tr><th class="text-right">氏名：</th><td>{{ payInCashItem?.member?.lastName }} {{ payInCashItem?.member?.firstName }}</td></tr>
              @for (transaction of payInCashTransactions; track transaction.id; let i = $index) {
                <tr><th class="text-right">{{ i + 1 }}：</th><td>{{ transaction.title }}&nbsp;¥{{ transaction.amount | number:'1.0-0' }}</td></tr>
              }
            </table>
          </div>
        </div>
        <div class="modal-footer" style="background-color: #FFF6F8 !important">
          <button class="btn bg-close text-white" type="button" (click)="showPayInCashModal = false">
            <i class="fa s-icon-cancel"></i>&nbsp;閉じる
          </button>
          <button class="btn btn-primary text-white" type="button" (click)="doPayInCash()">
            <i class="fa s-icon-turnOver"></i>&nbsp;変更
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- 削除確認モーダル -->
@if (showDeleteModal) {
  <div class="modal-backdrop fade show"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content border-danger">
        <div class="modal-header bg-danger">
          <h5 class="modal-title">売上の削除</h5>
          <button type="button" class="close" (click)="showDeleteModal = false">
            <span class="text-white" aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body" style="background-color: #FFF6F8 !important">
          <div class="form-group row">
            <label class="col-md-12 col-form-label">売上を削除します。よろしいですか。</label>
          </div>
        </div>
        <div class="modal-footer" style="background-color: #FFF6F8 !important">
          <button class="btn bg-close text-white" type="button" (click)="showDeleteModal = false">
            <i class="fa s-icon-cancel"></i>&nbsp;閉じる
          </button>
          <button class="btn btn-danger" type="button" (click)="doDelete()">
            <i class="fa s-icon-delete"></i>&nbsp;削除
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- 会員詳細モーダル -->
@if (showMemberDetailModal) {
  <div class="modal-backdrop fade show" (click)="showMemberDetailModal = false"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-labelledby="getMemberDetailModalLabel">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="getMemberDetailModalLabel">会員情報 / 詳細</h5>
          <button type="button" class="close" (click)="showMemberDetailModal = false">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            @if (!memberDetail) {
              <div class="text-center py-3"><span class="fa fa-spinner fa-spin fa-2x"></span></div>
            } @else {
              <table class="table table-sm table-borderless">
                <tr>
                  <th class="text-right" style="width: 40%;">会員ID：</th>
                  <td>{{ memberDetail.memberCode }}</td>
                </tr>
                <tr>
                  <th class="text-right">会員氏名（かな）：</th>
                  <td>{{ memberDetail.lastName }} {{ memberDetail.firstName }}({{ memberDetail.lastNameKana }} {{ memberDetail.firstNameKana }})</td>
                </tr>
                <tr>
                  <th class="text-right">生年月日：</th>
                  <td>{{ memberDetail.birthday }}</td>
                </tr>
                <tr>
                  <th class="text-right">住所：</th>
                  <td>〒{{ memberDetail.zip }}</td>
                </tr>
                <tr>
                  <th class="text-right"></th>
                  <td>{{ memberDetail.province }}&nbsp;{{ memberDetail.city }}</td>
                </tr>
                <tr>
                  <th class="text-right"></th>
                  <td>{{ memberDetail.area }}&nbsp;{{ memberDetail.address }}</td>
                </tr>
                <tr>
                  <th class="text-right">電話番号：</th>
                  <td>{{ memberDetail.phone }}</td>
                </tr>
                <tr>
                  <th class="text-right">メールアドレス：</th>
                  <td>{{ memberDetail.email }}</td>
                </tr>
                <tr>
                  <th class="text-right">コース区分：</th>
                  <td>{{ memberDetail.studioPlan?.name }}</td>
                </tr>
                <tr>
                  <th class="text-right">支払方法：</th>
                  <td>
                    @if (memberDetail.payment?.paymentMethod === 0) { <span>口座振替</span> }
                    @if (memberDetail.payment?.paymentMethod === 1) { <span>クレジット</span> }
                  </td>
                </tr>
                <tr>
                  <th class="text-right">入退館キー：</th>
                  <td>{{ memberDetail.keyNumber?.keyNumber }}</td>
                </tr>
                <tr>
                  <th class="text-right">ロッカー：</th>
                  <td>{{ memberDetail.lockerNumber?.lockerNumber }}</td>
                </tr>
                <tr>
                  <th class="text-right">登録日時：</th>
                  <td>{{ memberDetail.createdAt | date:'yyyy/MM/dd HH:mm' }}</td>
                </tr>
              </table>
            }
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="showMemberDetailModal = false">
            <i class="fa s-icon-cancel fa-fw"></i>&nbsp;閉じる
          </button>
        </div>
      </div>
    </div>
  </div>
}

@if (showEditTransactionModal && selectedTransactionId && selectedTransactionMemberId) {
  <app-transaction-edit
    [memberId]="selectedTransactionMemberId"
    [transactionId]="selectedTransactionId"
    [member]="selectedTransactionMember"
    [studio]="selectedStudio"
    (closed)="closeEditTransactionModal()"
    (saved)="refreshTransactions()">
  </app-transaction-edit>
}
  `
})
export class TransactionListComponent implements OnInit {
  private pageTitleService = inject(PageTitleService);
  private studioService = inject(StudioService);
  private memberService = inject(MemberService);
  private transactionService = inject(TransactionService);
  private invoiceService = inject(InvoiceService);
  private studioSelection = inject(StudioSelectionService);
  private flash = inject(FlashService);

  studios: any[] = [];
  selectedStudio: any = null;
  items: any[] = [];
  searching = false;
  downloading = false;
  showPayInCashModal = false;
  payInCashItem: any = null;
  payInCashTransactions: any[] = [];
  payInCashInvoice: any = null;
  showDeleteModal = false;
  deleteItem: any = null;
  showMemberDetailModal = false;
  memberDetail: any = null;
  showEditTransactionModal = false;
  selectedTransactionId: number | null = null;
  selectedTransactionMemberId: number | null = null;
  selectedTransactionMember: any = null;

  selectedYear: number;
  selectedMonth: number;
  chargeTypeFilter: any = null;
  paymentMethodFilter: any = null;
  statusFilter: any = null;
  keyword = '';

  readonly yearOptions = buildYearOptions();
  readonly monthOptions = MONTH_OPTIONS;
  readonly chargeTypeOptions = CHARGE_TYPE_OPTIONS;
  readonly paymentMethodOptions = PAYMENT_METHOD_OPTIONS;
  readonly statusOptions = STATUS_OPTIONS;
  readonly chargeTypeName = chargeTypeName;
  readonly pageLimit = 100;
  pageIndex = 0;
  private transactionLoadId = 0;

  constructor() {
    const now = new JstDate();
    this.selectedYear = now.getFullYear();
    this.selectedMonth = now.getMonth() + 1;
  }

  get showFilterOption(): boolean { return !!this.selectedStudio; }

  get filteredItems(): any[] {
    let list = this.items;
    list = list.filter(item => matchesNestedKeyword(item, this.keyword));
    if (this.chargeTypeFilter != null) {
      list = list.filter(t => t.chargeType === this.chargeTypeFilter.id);
    }
    if (this.paymentMethodFilter != null) {
      list = list.filter(t =>
        String(t.paymentMethod ?? t.member?.payment?.paymentMethod) === String(this.paymentMethodFilter.id)
      );
    }
    if (this.statusFilter != null) {
      list = list.filter(t => t.status === this.statusFilter.id);
    }
    return list;
  }

  get pagedItems(): any[] {
    return this.filteredItems.slice(this.pageIndex * this.pageLimit, (this.pageIndex + 1) * this.pageLimit);
  }
  get totalPages(): number {
    return Math.ceil(this.filteredItems.length / this.pageLimit);
  }
  goToPage(p: number): void {
    this.pageIndex = Math.max(0, Math.min(p, this.totalPages - 1));
  }

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('決済管理', 's-icon-settlement');
    this.studios = await this.studioService.list('all') ?? [];
    const restored = this.studioSelection.restore(this.studios, SESSION_KEY);
    if (restored) {
      this.selectedStudio = restored;
      await this.loadTransactions();
    }
  }

  async onStudioChange(): Promise<void> {
    this.transactionLoadId++;
    this.items = [];
    this.pageIndex = 0;
    if (!this.selectedStudio) { this.searching = false; this.studioSelection.clear(SESSION_KEY); return; }
    this.studioSelection.save(this.selectedStudio, SESSION_KEY);
    await this.loadTransactions();
  }

  async loadTransactions(): Promise<void> {
    if (!this.selectedStudio) return;
    const loadId = ++this.transactionLoadId;
    this.pageIndex = 0;
    this.items = [];
    this.searching = true;
    try {
      const items = await this.studioService.getTransaction(
        this.selectedStudio.id, this.selectedYear, this.selectedMonth
      ) ?? [];
      if (loadId === this.transactionLoadId) this.items = items;
    } finally {
      if (loadId === this.transactionLoadId) this.searching = false;
    }
  }

  async openPayInCashModal(item: any): Promise<void> {
    this.payInCashItem = item;
    this.payInCashTransactions = [];
    this.payInCashInvoice = null;
    // 旧版 transaction.js:276-294: modalは請求取得の完了を待たずに開く
    this.showPayInCashModal = true;
    if (item.invoice == null) {
      this.payInCashTransactions = [item];
    } else if (item.status === 0 || item.status === 3 || item.status === 6) {
      const invoice: any = await this.invoiceService.getOne(item.invoice.id);
      if (this.payInCashItem !== item) return;
      this.payInCashInvoice = invoice;
      this.payInCashTransactions = (invoice?.transactions ?? []).filter((t: any) => t.status === 0 || t.status === 3 || t.status === 6);
    }
  }

  async doPayInCash(): Promise<void> {
    const ids = this.payInCashTransactions.map(item => item.id).filter(Boolean);
    if (ids.length === 0) return;
    this.showPayInCashModal = false;
    await this.transactionService.update({ id: ids, status: 9 });
    if (this.payInCashInvoice?.id) {
      const invoiceId = this.payInCashInvoice.id;
      const updated = await this.invoiceService.updateStatus({ id: invoiceId, status: 9 });
      // 旧版 transaction.js:303-311: 請求の更新が偽なら通知（旧版はsuccess色の不具合のためdangerで表示、50秒）
      if (!updated) this.flash.create('danger', '請求データの更新に失敗しました。請求ID: ' + invoiceId, 50000);
    }
    this.payInCashItem = null;
    this.payInCashTransactions = [];
    this.payInCashInvoice = null;
    await this.loadTransactions();
  }

  openDeleteModal(item: any): void {
    this.deleteItem = item;
    this.showDeleteModal = true;
  }

  async doDelete(): Promise<void> {
    const invoiceId = this.deleteItem?.invoice?.id;
    this.showDeleteModal = false;
    await this.transactionService.remove(this.deleteItem.id);
    if (invoiceId) {
      await this.invoiceService.updateAmount({ id: invoiceId });
    }
    await this.loadTransactions();
  }

  clearFilter(): void {
    this.keyword = '';
    this.chargeTypeFilter = null;
    this.paymentMethodFilter = null;
    this.statusFilter = null;
    this.pageIndex = 0;
  }

  async openMemberDetail(memberId: number | null | undefined): Promise<void> {
    if (!memberId) return;
    this.memberDetail = null;
    this.showMemberDetailModal = true;
    this.memberDetail = await this.memberService.getOne(memberId);
  }

  openEditTransactionModal(item: any): void {
    this.selectedTransactionId = Number(item?.id) || null;
    this.selectedTransactionMemberId = Number(item?.member?.id) || null;
    this.selectedTransactionMember = item?.member ?? null;
    this.showEditTransactionModal = this.selectedTransactionId != null && this.selectedTransactionMemberId != null;
  }

  closeEditTransactionModal(): void {
    this.showEditTransactionModal = false;
    this.selectedTransactionId = null;
    this.selectedTransactionMemberId = null;
    this.selectedTransactionMember = null;
  }

  async refreshTransactions(): Promise<void> {
    await this.loadTransactions();
  }

  async downloadPaymentErrorList(): Promise<void> {
    if (this.downloading) return;
    this.downloading = true;
    const yearMonth = `${this.selectedYear}-${('0' + this.selectedMonth).slice(-2)}`;
    try {
      const data: any = await this.transactionService.getPaymentErrorData(this.selectedStudio.id, yearMonth);
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `${environment.apiBaseUrl}/invoice/excel`;
      form.style.display = 'none';
      const addField = (name: string, value: string) => {
        const input = document.createElement('input');
        input.type = 'text';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      };
      addField('paymentError', JSON.stringify(data));
      addField('selectIssueMonth', `${this.selectedYear}年${('0' + this.selectedMonth).slice(-2)}月`);
      addField('selectStudioName', this.selectedStudio.name);
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } finally {
      setTimeout(() => { this.downloading = false; }, 5000);
    }
  }
}
